import React, { useState } from "react";
import { Plus, X, Droplet, Zap, Flame, Home, Calendar, Wifi, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import AddMeterReadingModal from "./_addmeterreading";

export default function RecentMeterReadings({ recentReadings, apartments, isLoading }) {
  const [showAddModal, setShowAddModal] = useState(false);
  const [utilityType, setUtilityType] = useState("water");
  const [readingDate, setReadingDate] = useState("");
  const [meterReading, setMeterReading] = useState("");
  const [selectedProperty, setSelectedProperty] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission here
    console.log({
      utilityType,
      readingDate,
      meterReading,
      selectedProperty
    });
    setShowAddModal(false);
    // Reset form
    setUtilityType("water");
    setReadingDate("");
    setMeterReading("");
    setSelectedProperty("");
  };

  const getUtilityIcon = (utility) => {
    switch (utility.toLowerCase()) {
      case "water": return <Droplet className="w-4 h-4 text-blue-500" />;
      case "electricity": return <Zap className="w-4 h-4 text-yellow-500" />;
      case "internet": return <Wifi className="w-4 h-4 text-purple-500" />;
      case "garbage": return <Trash2 className="w-4 h-4 text-green-500" />;
      case "gas": return <Flame className="w-4 h-4 text-orange-500" />;
      default: return <Droplet className="w-4 h-4 text-blue-500" />;
    }
  };

  // Calculate pagination
  const totalPages = Math.ceil(recentReadings?.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = recentReadings?.slice(indexOfFirstItem, indexOfLastItem) || [];

  // Skeleton loader for table rows
  const SkeletonRow = () => (
    <tr className="animate-pulse">
      <td className="px-6 py-4 whitespace-nowrap"><div className="bg-gray-200 rounded w-3/4 h-4"></div></td>
      <td className="px-6 py-4 whitespace-nowrap"><div className="bg-gray-200 rounded w-1/2 h-4"></div></td>
      <td className="px-6 py-4 whitespace-nowrap"><div className="bg-gray-200 rounded w-1/4 h-4"></div></td>
      <td className="px-6 py-4 whitespace-nowrap"><div className="bg-gray-200 rounded w-1/4 h-4"></div></td>
      <td className="px-6 py-4 whitespace-nowrap"><div className="bg-gray-200 rounded w-1/2 h-4"></div></td>
    </tr>
  );

  return (
    <div className="md:col-span-4 bg-white shadow-sm p-6 border border-gray-100 rounded-lg">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="font-semibold text-gray-800 text-lg">Recent Meter Readings</h2>
          <p className="text-gray-500 text-sm">Latest utility readings across properties</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded-md text-white text-sm transition-colors"
        >
          <Plus className="mr-2 w-4 h-4" />
          Add New Reading
        </button>
      </div>

      {/* Readings Table */}
      <div className="border border-gray-200 rounded-lg overflow-x-auto">
        <table className="divide-y divide-gray-200 min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Unit Number</th>
              <th scope="col" className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Utility</th>
              <th scope="col" className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Units Consumed</th>
              <th scope="col" className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Cost</th>
              <th scope="col" className="px-6 py-3 font-medium text-gray-500 text-xs text-left uppercase tracking-wider">Date Recorded</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <>
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
                <SkeletonRow />
              </>
            ) : currentItems.length > 0 ? (
              currentItems.map((reading) => (
                <tr key={reading.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-start gap-2">
                      <Home className="justify-center items-center mt-1 w-4 h-4 text-gray-500" />
                      <div>
                        <span className="font-medium text-gray-900 text-sm">{reading.house}</span>
                        <div className="text-gray-500 text-xs">{reading.property}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {getUtilityIcon(reading.expense_name.toLowerCase())}
                      <span className="ml-2 text-gray-600 text-sm capitalize">{reading.expense_name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="font-medium text-gray-900 text-sm">{reading.units_consumed}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="font-medium text-gray-900 text-sm">  KES {reading.cost?.toLocaleString('en-US')}                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-gray-500 text-sm">{new Date(reading.readings_date).toLocaleDateString()}</span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="px-6 py-4 text-gray-500 text-center">
                  No meter readings available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {recentReadings?.length > itemsPerPage && (
        <div className="flex justify-between items-center mt-4 px-2">
          <div className="text-gray-500 text-sm">
            Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
            <span className="font-medium">
              {Math.min(indexOfLastItem, recentReadings.length)}
            </span>{' '}
            of <span className="font-medium">{recentReadings.length}</span> results
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`p-1 rounded-md border ${currentPage === 1 ? 'text-gray-400 bg-gray-100 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-8 h-8 rounded-md text-sm ${currentPage === page ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className={`p-1 rounded-md border ${currentPage === totalPages ? 'text-gray-400 bg-gray-100 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}


      <AddMeterReadingModal
       isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        apartments={apartments}
        onSubmit={handleSubmit}
      />
    </div>
  );
}